/**
 * Booking General Information Form
 * Editable: Customer info, dates, duration
 * Read-only: Order ID, location, timestamps, sales channel
 */

import { useState, useEffect } from 'react';
import { Calendar, User, Mail, Phone, Smartphone, MessageSquare, Clock, MapPin, CheckCircle, XCircle, MinusCircle, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import NumberInput from './NumberInput';
import { useDateFormatter } from '../hooks/useDateFormatter';
import { useLanguage, LANGUAGE_CONFIGS } from '../contexts/LanguageContext';
import { useMerchant } from '../contexts/MerchantContext';
import { asZoned, zonedWallTimeToUtc } from '../lib/timezone';
import { telHref, smsHref, whatsappHref } from '../utils/phone';

import type { Database } from '../lib/database.types';
import { sanitizeCustomFieldHtml } from '../lib/customFields';

type Booking = Database['public']['Tables']['bookings']['Row'];

interface BookingGeneralFormProps {
  booking: Booking & { locations?: any };
  onChange: (updates: Partial<Booking>) => void;
  disabled: boolean;
}

export default function BookingGeneralForm({ booking, onChange, disabled }: BookingGeneralFormProps) {
  const { t } = useTranslation();
  const { availableLanguages } = useLanguage();
  const { merchantLocale } = useMerchant();
  const { timezone } = merchantLocale;
  // Which field the user drives. Default is "duration" (the day/hour inputs are
  // the editable truth; the end date is the derived read-out). Switching to
  // "end" makes the end date/time editable and the duration derived instead.
  const [editMode, setEditMode] = useState<'end' | 'duration'>('duration');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [durationDays, setDurationDays] = useState(0);
  const [durationHours, setDurationHours] = useState(0);

  const pad = (n: number) => String(n).padStart(2, '0');

  // Derive the wall-clock date/time input values straight from the booking
  // prop. Used as the *uncontrolled* inputs' defaultValue so React never
  // re-asserts the value mid-entry (which would wipe a half-typed date when
  // tabbing between day/month/year segments). The booking is always loaded
  // before this form mounts, so these are correct at mount; the inputs
  // re-initialise when the form remounts (on load/save) or the end block
  // remounts (on edit-mode toggle).
  const toDateInputValue = (iso: string | null | undefined) => {
    if (!iso) return '';
    const z = asZoned(new Date(iso), timezone);
    return `${z.getFullYear()}-${pad(z.getMonth() + 1)}-${pad(z.getDate())}`;
  };
  const toTimeInputValue = (iso: string | null | undefined) => {
    if (!iso) return '';
    const z = asZoned(new Date(iso), timezone);
    return `${pad(z.getHours())}:${pad(z.getMinutes())}`;
  };

  useEffect(() => {
    // Project the stored UTC instants onto the merchant's wall-clock so the
    // date/time inputs match what the rest of the page renders via
    // useDateFormatter (which also uses merchant timezone).
    if (booking.start_date) {
      const z = asZoned(new Date(booking.start_date), timezone);
      setStartDate(`${z.getFullYear()}-${pad(z.getMonth() + 1)}-${pad(z.getDate())}`);
      setStartTime(`${pad(z.getHours())}:${pad(z.getMinutes())}`);
    }
    if (booking.end_date) {
      const z = asZoned(new Date(booking.end_date), timezone);
      setEndDate(`${z.getFullYear()}-${pad(z.getMonth() + 1)}-${pad(z.getDate())}`);
      setEndTime(`${pad(z.getHours())}:${pad(z.getMinutes())}`);
    }
    if (booking.start_date && booking.end_date) {
      // Duration is instant arithmetic — TZ-independent. Rounded to whole
      // hours because the day/hour inputs can't express minutes.
      const start = new Date(booking.start_date);
      const end = new Date(booking.end_date);
      const totalHours = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60));
      setDurationDays(Math.max(0, Math.floor(totalHours / 24)));
      setDurationHours(Math.max(0, totalHours % 24));
    }
  }, [booking.start_date, booking.end_date, timezone]);

  // --- Start edits ---------------------------------------------------------
  // In "duration" mode the end follows start + duration. In "end" mode the end
  // is the editable truth and stays put, so moving the start only shifts start.
  const emitStart = (date: string, time: string) => {
    const startDateTime = zonedWallTimeToUtc(date, time, timezone);
    if (isNaN(startDateTime.getTime())) return;
    if (editMode === 'duration') {
      const totalHours = durationDays * 24 + durationHours;
      const endDateTime = new Date(startDateTime.getTime() + totalHours * 60 * 60 * 1000);
      onChange({ start_date: startDateTime.toISOString(), end_date: endDateTime.toISOString() });
    } else {
      onChange({ start_date: startDateTime.toISOString() });
    }
  };

  // Ignore empty values. A native date/time input reports "" while a partial
  // keyboard entry is still incomplete (e.g. day + month typed, year not yet);
  // pushing that "" back into the controlled value makes the browser discard
  // the half-typed segments. Skipping it lets the entry complete.
  const handleStartDateChange = (date: string) => { if (!date) return; setStartDate(date); emitStart(date, startTime); };
  const handleStartTimeChange = (time: string) => { if (!time) return; setStartTime(time); emitStart(startDate, time); };

  // --- End edits (end mode) ------------------------------------------------
  const emitEnd = (date: string, time: string) => {
    const endDateTime = zonedWallTimeToUtc(date, time, timezone);
    if (isNaN(endDateTime.getTime())) return;
    onChange({ end_date: endDateTime.toISOString() });
  };

  const handleEndDateChange = (date: string) => { if (!date) return; setEndDate(date); emitEnd(date, endTime); };
  const handleEndTimeChange = (time: string) => { if (!time) return; setEndTime(time); emitEnd(endDate, time); };

  // --- Duration edits (duration mode) --------------------------------------
  // Editing the duration rounds the end to a whole hour, since minutes can't
  // be represented in the day/hour inputs.
  const emitDuration = (days: number, hours: number) => {
    if (!startDate || !startTime) return;
    const startDateTime = zonedWallTimeToUtc(startDate, startTime, timezone);
    if (isNaN(startDateTime.getTime())) return;
    const totalHours = days * 24 + hours;
    const endDateTime = new Date(startDateTime.getTime() + totalHours * 60 * 60 * 1000);
    onChange({ start_date: startDateTime.toISOString(), end_date: endDateTime.toISOString() });
  };

  const handleDurationDaysChange = (days: number) => { setDurationDays(days); emitDuration(days, durationHours); };
  const handleDurationHoursChange = (hours: number) => { setDurationHours(hours); emitDuration(durationDays, hours); };

  // Derived duration read-out (incl. minutes) shown in "end" mode.
  const durationReadout = (() => {
    if (!booking.start_date || !booking.end_date) return t('common.na');
    const ms = new Date(booking.end_date).getTime() - new Date(booking.start_date).getTime();
    if (isNaN(ms) || ms < 0) return t('common.na');
    const totalMin = Math.round(ms / 60000);
    const d = Math.floor(totalMin / 1440);
    const h = Math.floor((totalMin % 1440) / 60);
    const m = totalMin % 60;
    const parts: string[] = [];
    if (d) parts.push(`${d}${t('bookingGeneral.durationDaysShort')}`);
    if (h) parts.push(`${h}${t('bookingGeneral.durationHoursShort')}`);
    if (m) parts.push(`${m}${t('bookingGeneral.durationMinutesShort')}`);
    return parts.length ? parts.join(' ') : `0${t('bookingGeneral.durationHoursShort')}`;
  })();

  const { formatDateTime } = useDateFormatter();
  const formatDate = (dateString: string | null) => dateString ? formatDateTime(dateString) : t('common.na');

  const locationName = booking.locations?.location_name || t('bookingGeneral.unknownLocation');

  return (
    <div className="space-y-lg">
      {/* Read-Only Info Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-lg p-md bg-tertiary rounded-lg">
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.orderId')}</label>
          <div className="text-primary font-semibold">{booking.order_id}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">
            <MapPin className="w-4 h-4 inline mr-xs" />
            {t('bookingGeneral.location')}
          </label>
          <div className="text-primary">{locationName}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.salesChannel')}</label>
          <div className="text-primary">{booking.sales_channel || t('common.na')}</div>
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.createdAt')}</label>
          <div className="text-primary text-sm">{formatDate(booking.created_at)}</div>
        </div>
      </div>

      {/* Customer Information */}
      <div className="border-t border-primary pt-lg">
        <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
          <User className="w-5 h-5 text-blue" />
          {t('bookingGeneral.customerInformation')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingGeneral.firstName')} *
            </label>
            <input
              type="text"
              value={booking.customer_firstname}
              onChange={(e) => onChange({ customer_firstname: e.target.value })}
              disabled={disabled}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingGeneral.lastName')} *
            </label>
            <input
              type="text"
              value={booking.customer_lastname}
              onChange={(e) => onChange({ customer_lastname: e.target.value })}
              disabled={disabled}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              <Mail className="w-4 h-4 inline mr-xs" />
              {t('bookingGeneral.email')} *
            </label>
            <input
              type="email"
              value={booking.customer_email}
              onChange={(e) => onChange({ customer_email: e.target.value })}
              disabled={disabled}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              <Phone className="w-4 h-4 inline mr-xs" />
              {t('bookingGeneral.phone')} *
            </label>
            <div className="flex gap-xs">
              <input
                type="tel"
                value={booking.customer_phone}
                onChange={(e) => onChange({ customer_phone: e.target.value })}
                disabled={disabled}
                className="input flex-1 min-w-0"
                required
              />
              {(() => {
                const baseClasses =
                  'flex-shrink-0 inline-flex items-center justify-center px-md rounded-lg border border-primary bg-tertiary text-primary transition-colors';
                const disabledClasses = `${baseClasses} opacity-50 cursor-not-allowed`;

                const callHref = telHref(booking.customer_phone);
                const smsLink = smsHref(booking.customer_phone);
                const waLink = whatsappHref(booking.customer_phone);
                const callLabel = t('bookingGeneral.callCustomer');
                const smsLabel = t('bookingGeneral.smsCustomer');
                const waLabel = t('bookingGeneral.whatsappCustomer');

                return (
                  <>
                    {callHref ? (
                      <a
                        href={callHref}
                        aria-label={callLabel}
                        title={callLabel}
                        className={`${baseClasses} hover:bg-blue/10 hover:text-blue`}
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                    ) : (
                      <button type="button" disabled aria-label={callLabel} title={callLabel} className={disabledClasses}>
                        <Phone className="w-4 h-4" />
                      </button>
                    )}
                    {smsLink ? (
                      <a
                        href={smsLink}
                        aria-label={smsLabel}
                        title={smsLabel}
                        className={`${baseClasses} hover:bg-blue/10 hover:text-blue`}
                      >
                        <Smartphone className="w-4 h-4" />
                      </a>
                    ) : (
                      <button type="button" disabled aria-label={smsLabel} title={smsLabel} className={disabledClasses}>
                        <Smartphone className="w-4 h-4" />
                      </button>
                    )}
                    {waLink ? (
                      <a
                        href={waLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={waLabel}
                        title={waLabel}
                        className={`${baseClasses} hover:bg-success/10 hover:text-success`}
                      >
                        <MessageSquare className="w-4 h-4" />
                      </a>
                    ) : (
                      <button type="button" disabled aria-label={waLabel} title={waLabel} className={disabledClasses}>
                        <MessageSquare className="w-4 h-4" />
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Marketing Consent */}
        <div className="mt-md">
          <label className="flex items-center gap-md cursor-pointer">
            <input
              type="checkbox"
              checked={booking.marketing_consent ?? false}
              onChange={(e) => onChange({ marketing_consent: e.target.checked })}
              disabled={disabled}
              className="h-5 w-5 rounded border-gray-300 text-blue focus:ring-blue"
            />
            <span className="text-sm text-primary flex items-center gap-xs">
              {t('bookingGeneral.marketingConsent')}
              {booking.marketing_consent === null ? (
                <MinusCircle className="w-4 h-4 text-secondary" />
              ) : booking.marketing_consent ? (
                <CheckCircle className="w-4 h-4 text-success" />
              ) : (
                <XCircle className="w-4 h-4 text-error" />
              )}
            </span>
          </label>
        </div>
      </div>

      {/* Booking Dates & Duration */}
      <div className="border-t border-primary pt-lg">
        <div className="flex items-center justify-between mb-md flex-wrap gap-sm">
          <h3 className="text-lg font-semibold text-primary flex items-center gap-xs">
            <Calendar className="w-5 h-5 text-blue" />
            {t('bookingGeneral.bookingPeriod')}
          </h3>
          {/* Edit-mode toggle: drive by end date or by duration */}
          <div className="inline-flex rounded-lg border border-primary overflow-hidden text-sm">
            <button
              type="button"
              onClick={() => setEditMode('end')}
              className={`px-md py-xs font-medium transition-colors ${editMode === 'end' ? 'bg-blue text-white' : 'bg-secondary text-secondary'}`}
            >
              {t('bookingGeneral.editByEndDate')}
            </button>
            <button
              type="button"
              onClick={() => setEditMode('duration')}
              className={`px-md py-xs font-medium transition-colors ${editMode === 'duration' ? 'bg-blue text-white' : 'bg-secondary text-secondary'}`}
            >
              {t('bookingGeneral.editByDuration')}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingGeneral.startDate')} *
            </label>
            <input
              type="date"
              defaultValue={toDateInputValue(booking.start_date)}
              onChange={(e) => handleStartDateChange(e.target.value)}
              disabled={disabled}
              className="input w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingGeneral.startTime')} *
            </label>
            <input
              type="time"
              defaultValue={toTimeInputValue(booking.start_date)}
              onChange={(e) => handleStartTimeChange(e.target.value)}
              disabled={disabled}
              className="input w-full"
              required
            />
          </div>
          {editMode === 'duration' ? (
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                <Clock className="w-4 h-4 inline mr-xs" />
                {t('bookingGeneral.duration')} *
              </label>
              <div className="flex items-center gap-sm">
                <div className="flex items-center gap-xs">
                  <span className="text-sm text-secondary">{t('bookingGeneral.days')}:</span>
                  <NumberInput
                    value={durationDays}
                    onChange={(v) => handleDurationDaysChange(v)}
                    mode="int"
                    min={0}
                    disabled={disabled}
                    className="input w-20"
                    data-testid="booking-general-duration-days-input"
                  />
                </div>
                <div className="flex items-center gap-xs">
                  <span className="text-sm text-secondary">{t('bookingGeneral.hours')}:</span>
                  <NumberInput
                    value={durationHours}
                    onChange={(v) => handleDurationHoursChange(v)}
                    mode="int"
                    min={0}
                    max={23}
                    disabled={disabled}
                    className="input w-20"
                    data-testid="booking-general-duration-hours-input"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                <Clock className="w-4 h-4 inline mr-xs" />
                {t('bookingGeneral.duration')}
              </label>
              <div className="input w-full bg-tertiary flex items-center text-primary font-medium" data-testid="booking-general-duration-readout">
                {durationReadout}
              </div>
            </div>
          )}
        </div>

        {editMode === 'end' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-lg mt-md">
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingGeneral.endDate')} *
              </label>
              <input
                type="date"
                defaultValue={toDateInputValue(booking.end_date)}
                onChange={(e) => handleEndDateChange(e.target.value)}
                disabled={disabled}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary mb-xs">
                {t('bookingGeneral.endTime')} *
              </label>
              <input
                type="time"
                defaultValue={toTimeInputValue(booking.end_date)}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                disabled={disabled}
                className="input w-full"
                required
              />
            </div>
          </div>
        )}

        {editMode === 'duration' && (
          <p className="text-xs text-secondary mt-xs">{t('bookingGeneral.durationRoundingHint')}</p>
        )}

        {(() => {
          const showEndTile = editMode === 'duration';
          const tileCount = (showEndTile ? 1 : 0) + (booking.returned_at ? 1 : 0) + (booking.code_gate ? 1 : 0);
          if (tileCount === 0) return null;
          const gridCols = tileCount === 1
            ? 'grid-cols-1'
            : tileCount === 2
              ? 'grid-cols-1 md:grid-cols-2'
              : 'grid-cols-1 md:grid-cols-3';
          return (
            <div className={`mt-md grid ${gridCols} gap-md`}>
              {showEndTile && (
                <div className="p-md bg-tertiary rounded-lg">
                  <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.endDateCalculated')}</label>
                  <div className="text-primary font-medium">{formatDate(booking.end_date)}</div>
                </div>
              )}
              {booking.returned_at && (
                <div className="p-md bg-tertiary rounded-lg">
                  <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.returnedAt')}</label>
                  <div className="flex items-center gap-xs text-primary font-medium">
                    <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                    {formatDate(booking.returned_at)}
                  </div>
                </div>
              )}
              {booking.code_gate && (
                <div className="p-md bg-tertiary rounded-lg">
                  <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.gateCode')}</label>
                  <div className="text-primary font-medium">{booking.code_gate}</div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Language */}
      <div className="border-t border-primary pt-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
          <div>
            <label className="block text-sm font-medium text-primary mb-xs">
              {t('bookingGeneral.language')}
            </label>
            <select
              value={booking.language || 'en'}
              onChange={(e) => onChange({ language: e.target.value })}
              disabled={disabled}
              className="input w-full"
            >
              {availableLanguages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
              {/* Show current booking language even if not in merchant's supported list */}
              {booking.language && !availableLanguages.some((l) => l.code === booking.language) && (
                <option value={booking.language}>
                  {LANGUAGE_CONFIGS[booking.language]?.label || booking.language.toUpperCase()}
                </option>
              )}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-secondary mb-xs">{t('bookingGeneral.updatedAt')}</label>
            <div className="text-primary text-sm mt-2">{formatDate(booking.updated_at)}</div>
          </div>
        </div>
      </div>

      {/* Custom Field Values */}
      {booking.custom_field_values && typeof booking.custom_field_values === 'object' && Object.keys(booking.custom_field_values as Record<string, any>).length > 0 && (
        <div className="border-t border-primary pt-lg">
          <h3 className="text-lg font-semibold text-primary mb-md flex items-center gap-xs">
            <FileText className="w-5 h-5 text-blue" />
            {t('bookingGeneral.customFields')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-lg">
            {Object.entries(booking.custom_field_values as Record<string, string>).map(([key, value]) => {
              // Try to find label from location's custom_fields config
              const customFields = (booking.locations as any)?.custom_fields;
              const fieldConfig = Array.isArray(customFields)
                ? customFields.find((f: any) => f.key === key)
                : null;
              const label = fieldConfig?.label || key;
              // Multi-line answers keep their line breaks.
              const multiline = fieldConfig?.type === 'textarea';

              return (
                <div key={key} className="p-md bg-tertiary rounded-lg">
                  <label
                    className="block text-sm font-medium text-secondary mb-xs"
                    dangerouslySetInnerHTML={{ __html: sanitizeCustomFieldHtml(label) }}
                  />
                  <div className={`text-primary font-medium ${multiline ? 'whitespace-pre-line break-words' : ''}`}>{value || t('common.na')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
